import React, { useCallback, useEffect, useRef } from 'react';

import { cn } from '@lyearn/style';
import { Divider } from '@lyearn/ui';
import { Link } from '@/components/Link';

import {
  SIDEBAR_FOCUS_RING,
  SIDEBAR_LINE,
  SIDEBAR_ROW_ACTIVE,
  SIDEBAR_ROW_DISABLED,
  SIDEBAR_ROW_FOCUS,
  SIDEBAR_ROW_REST,
  SIDEBAR_TEXT,
  SIDEBAR_TEXT_DISABLED,
} from '../theme';
import { SidebarNavSubItem } from '../types';
import { isSidebarItemActive } from '../utils';

const ROW_CLASSES = cn(
  'flex h-32 flex-shrink-0 items-center overflow-hidden truncate rounded-8 px-8',
  SIDEBAR_TEXT,
);

const MOVEMENT_KEYS = ['ArrowDown', 'ArrowUp', 'End', 'Home'];

const toGroups = (items: SidebarNavSubItem[]) =>
  items.reduce<SidebarNavSubItem[][]>((groups, item) => {
    const currentGroup = groups[groups.length - 1];

    if (currentGroup && currentGroup[0].groupId === item.groupId) {
      currentGroup.push(item);

      return groups;
    }

    return groups.concat([[item]]);
  }, []);

interface FlyoutPanelProps {
  currentPath: string;
  items: SidebarNavSubItem[];
  label: string;
  shouldAutoFocus?: boolean;
  triggerElement: HTMLElement | null;
  onClose: () => void;
}

const FlyoutPanel = (props: FlyoutPanelProps) => {
  const { currentPath, items, label, shouldAutoFocus, triggerElement, onClose } = props;

  const rootRef = useRef<HTMLDivElement>(null);

  const getRows = useCallback(
    () => Array.from(rootRef.current?.querySelectorAll<HTMLElement>('a[href]') ?? []),
    [],
  );

  const dismiss = useCallback(() => {
    const shouldRestoreFocus =
      !!rootRef.current?.contains(document.activeElement) ||
      document.activeElement === triggerElement;

    onClose();

    if (shouldRestoreFocus) {
      triggerElement?.focus();
    }
  }, [onClose, triggerElement]);

  useEffect(() => {
    if (shouldAutoFocus) {
      getRows()[0]?.focus();
    }
  }, [getRows, shouldAutoFocus]);

  useEffect(() => {
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };

    document.addEventListener('keydown', onDocumentKeyDown);

    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [dismiss]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        dismiss();

        return;
      }

      if (!MOVEMENT_KEYS.includes(event.key)) {
        return;
      }

      const rows = getRows();

      if (!rows.length) {
        return;
      }

      event.preventDefault();

      const currentIndex = rows.indexOf(document.activeElement as HTMLElement);
      const lastIndex = rows.length - 1;

      switch (event.key) {
        case 'ArrowDown':
          rows[currentIndex >= lastIndex ? 0 : currentIndex + 1].focus();
          break;
        case 'ArrowUp':
          rows[currentIndex <= 0 ? lastIndex : currentIndex - 1].focus();
          break;
        case 'Home':
          rows[0].focus();
          break;
        default:
          rows[lastIndex].focus();
      }
    },
    [dismiss, getRows],
  );

  const renderRow = (item: SidebarNavSubItem) => {
    const isActive = isSidebarItemActive(currentPath, item);
    const isDisabled = item.isDisabled || !item.to;

    if (isDisabled) {
      return (
        <span key={item.id} aria-disabled="true" className={cn(ROW_CLASSES, SIDEBAR_ROW_DISABLED)}>
          <span className="truncate">{item.text}</span>
        </span>
      );
    }

    return (
      <Link
        key={item.id}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          ROW_CLASSES,
          SIDEBAR_FOCUS_RING,
          isActive ? SIDEBAR_ROW_ACTIVE : cn(SIDEBAR_ROW_REST, SIDEBAR_ROW_FOCUS),
        )}
        to={item.to}
        onClick={onClose}>
        <span className="truncate">{item.text}</span>
      </Link>
    );
  };

  return (
    <div
      ref={rootRef}
      aria-label={label}
      className="flex flex-col gap-8 p-12"
      role="navigation"
      onKeyDown={onKeyDown}>
      <div className={cn('px-8', SIDEBAR_TEXT, SIDEBAR_TEXT_DISABLED)}>{label}</div>
      {toGroups(items).map((group, index) => (
        <React.Fragment key={group[0].groupId ?? group[0].id}>
          {index > 0 ? <Divider className={cn('flex-shrink-0', SIDEBAR_LINE)} /> : null}
          <div className="flex flex-col">{group.map(renderRow)}</div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default FlyoutPanel;
